import { atom } from 'jotai'
import { atomWithReset } from 'jotai/utils'

import { answeredElementsAtom } from '@/_atoms/answer';
import { pageElementsAtom, surveyElementsAtom, surveyViewAtom } from '@/_atoms/survey';
import { skippedStatusAtomFamily, skippedEmailAddressVerificationAtomFamily } from '@/_atoms/demographic';
import { getElementContext } from '@/_helpers/navHelpers';

import type { ContextNav, Element, ElementContext, StepInfo, StepNav, SurveyView } from '@/types/survey';

/** The index of the currently active element. */
export const elementIndexAtom = atomWithReset<number>(0);

/** Record whether the user has an active internet connection */
export const hasInternetConnectionAtom = atomWithReset<boolean>(true);

/** Whether the user is moving forward in the survey. When false, the user is moving backward (to previous items). */
export const isAdvancingAtom = atom<boolean>((get) => {
  const currentContext: ElementContext | null = get(contextNavAtom).currentContext;
  const prevContext: ElementContext | null = get(prevContextAtom);

  if (!currentContext || !prevContext) return true;
  return currentContext.index >= prevContext.index;
});

/** A string with format h:mm:ss or m:ss showing the amount of time remaining to complete a given step. */
export const timeRemainingAtom = atomWithReset<string | null>(null);

/**
 * Atom returning context objects relevant to the current element.
 */
export const contextNavAtom = atom<ContextNav>((get) => {
  const surveyView: SurveyView | null | undefined = get(surveyViewAtom).data;
  const emptyResult = {
    currentContext: null,
    nextElementContext: null,
    prevElementContext: null,
    firstElementOnNextPageContext: null,
    firstElementOnNextStepContext: null,
    lastElementOnPrevPageContext: null,
    lastElementOnPrevStepContext: null,
  }

  if (!surveyView) return emptyResult;

  const { elements, contextMap } = surveyView;
  const currentIndex: number = get(elementIndexAtom);

  const currentContext: ElementContext | null = getElementContext(currentIndex, elements, contextMap);
  if (!currentContext) return emptyResult;

  return {
    currentContext: currentContext,
    nextElementContext: getElementContext(currentIndex + 1, elements, contextMap),
    prevElementContext: getElementContext(currentIndex - 1, elements, contextMap),
    firstElementOnNextPageContext: getElementContext(currentContext.pageEnd + 1, elements, contextMap),
    firstElementOnNextStepContext: getElementContext(currentContext.stepEnd + 1, elements, contextMap),
    lastElementOnPrevPageContext: getElementContext(currentContext.pageStart - 1, elements, contextMap),
    lastElementOnPrevStepContext: getElementContext(currentContext.stepStart - 1, elements, contextMap),
  }
});

export const prevContextAtom = atom<ElementContext | null>(null);

/**
 * Atom returning the step objects for the current, previous, and next steps.
 */
export const stepNavAtom = atom<StepNav>((get) => {
  const stepMap: Map<string, StepInfo> | undefined = get(surveyViewAtom).data?.stepMap;
  const { currentContext, firstElementOnNextStepContext, lastElementOnPrevStepContext } = get(contextNavAtom);
  const emptyResult = { currentStep: null, nextStep: null, prevStep: null }
  if (!stepMap) return emptyResult;

  return {
    currentStep: currentContext ? (stepMap.get(currentContext.step) ?? null) : null,
    nextStep: firstElementOnNextStepContext ? (stepMap.get(firstElementOnNextStepContext.step) ?? null) : null,
    prevStep: lastElementOnPrevStepContext ? (stepMap.get(lastElementOnPrevStepContext.step) ?? null) : null,
  };
});

/**
 * Atom returning the index of the last answered element in the current step.
 * Returns `null` if no answered element exists in the step or if the context is invalid.
 */
export const indexOfLastAnsweredAtom = atom<number | null>((get) => {
  const currentContext: ElementContext | null = get(contextNavAtom).currentContext;
  if (!currentContext) return null;

  const answered: Set<string> = get(answeredElementsAtom);
  const elements: Element[] = get(surveyElementsAtom);

  // Guard against invalid step bounds
  if (currentContext.stepStart < 0 || currentContext.stepEnd >= elements.length) return null;

  for (let i = currentContext.stepEnd; i >= currentContext.stepStart; i--) {
    if (answered.has(elements[i].id)) {
      return i;
    }
  }

  return null;
});

/**
 * Atom that computes the index of the next unanswered element within the current step.
 *
 * Returns `null` if:
 * - all future elements are answered or skipped, or
 * - the context is invalid (out of bounds or empty survey).
 */
export const indexOfNextUnansweredAtom = atom<number | null>((get) => {
  const currentContext: ElementContext | null = get(contextNavAtom).currentContext;
  if (!currentContext) return null;

  const answered: Set<string> = get(answeredElementsAtom);
  const currentIndex: number = get(elementIndexAtom);
  const elements: Element[] = get(surveyElementsAtom);

  // Clamp defensively to the bounds of the step and survey
  const maxIndex = Math.min(currentContext.stepEnd, elements.length - 1);
  const minIndex = Math.max(currentContext.stepStart, 0);

  // Guard against invalid bounds
  if (currentIndex < minIndex || currentIndex > maxIndex) return null;

  // Walk forward, returning the first unanswered & unskipped element
  for (let i = currentIndex + 1; i <= maxIndex; i++) {
    const el: Element = elements[i];

    // Skip already-answered elements
    if (answered.has(el.id)) continue;

    // Special case: skipped email address verification fields
    if (get(skippedEmailAddressVerificationAtomFamily(el))) continue;

    // Return if the element is not skipped
    if (get(skippedStatusAtomFamily(el))) continue;

    return i;
  }

  return null;
});

/**
 * Atom that computes the index of the nearest prior unanswered element within the current step.
 *
 * Returns `null` if:
 * - all prior items and fields are answered or skipped, or
 * - the context is invalid (out of bounds or empty survey).
 */
export const indexOfPriorUnansweredAtom = atom<number | null>((get) => {
  const currentContext: ElementContext | null = get(contextNavAtom).currentContext;
  if (!currentContext) return null;

  const answered: Set<string> = get(answeredElementsAtom);
  const currentIndex: number = get(elementIndexAtom);
  const elements: Element[] = get(surveyElementsAtom);

  // Clamp defensively to the bounds of the step and survey
  const maxIndex = Math.min(currentContext.stepEnd, elements.length - 1);
  const minIndex = Math.max(currentContext.stepStart, 0);

  // Guard against unloaded/empty surveys or out-of-bounds indices
  if (currentIndex < minIndex || currentIndex > maxIndex) return null;

  // Walk backward, returning the first unanswered & unskipped element that isn't an instruction
  for (let i = currentIndex - 1; i >= minIndex; i--) {
    const el: Element = elements[i];

    // Skip instructions and already-answered elements
    if (('body' in el) || answered.has(el.id)) continue;

    // Special case: email address verification fields that are skippable
    if (get(skippedEmailAddressVerificationAtomFamily(el))) continue;

    // Continue if the element is skipped
    if (get(skippedStatusAtomFamily(el))) continue;

    return i;
  }
  return null;
});


/**
 * Atom that determines whether to show a "Jump to next unanswered" navigation link.
 *
 * Conditions:
 * - At least one element has been answered (`lastAnswered` is not null).
 * - The last answered element is positioned beyond the end of the current page.
 */
export const hasJumpForwardNavAtom = atom<boolean>((get) => {
  const indexOfNextUnanswered: number | null = get(indexOfNextUnansweredAtom);
  const lastAnswered: number | null = get(indexOfLastAnsweredAtom);
  const currentContext: ElementContext | null = get(contextNavAtom).currentContext;

  if (indexOfNextUnanswered === null || lastAnswered === null || !currentContext) return false;

  return lastAnswered >= currentContext.pageEnd;
});

/**
 * Determines whether to display a "Skip" navigation link, allowing the user to advance to the next element without
 * answering the current one.
 *
 * Conditions:
 * - The current step must allow skipping, OR jump-forward navigation must be available.
 * - Instructions never allow skipping.
 */
export const hasSkipNavAtom = atom<boolean>((get) => {
  const currentStep: StepInfo | null = get(stepNavAtom).currentStep;
  if (!currentStep) return false;

  const hasJumpForwardNav: boolean = get(hasJumpForwardNavAtom);

  // Instructions have a `body` property; if present on the page, skipping is disallowed.
  const pageContainsInstructions: boolean = get(pageElementsAtom).some((el) => 'body' in el);

  return !pageContainsInstructions && (currentStep.isSkippingAllowed || hasJumpForwardNav);
});

/**
 * Whether to display a "back" navigation link letting the user return to the prior element.
 *
 *  Conditions:
 *  - Backward navigation must be allowed for the current step, AND
 *  - Either:
 *     - There are earlier elements within the current step, OR
 *     - The previous step exists and is re-enterable.
 */
export const hasBackNavAtom = atom<boolean>((get) => {
  const elementIndex: number = get(elementIndexAtom);
  const currentContext: ElementContext | null = get(contextNavAtom).currentContext;
  const { currentStep, prevStep } = get(stepNavAtom)
  if (!currentStep || !currentContext) return false;

  const isBackwardNavigationAllowed: boolean = currentStep.isBackwardNavigationAllowed ?? false;
  const canReenterPreviousStep: boolean = prevStep?.canReenterAfterCompletion ?? false;
  const hasPriorElementsInStep: boolean = elementIndex > currentContext.stepStart;

  return isBackwardNavigationAllowed && (hasPriorElementsInStep || canReenterPreviousStep);
});

/**
 * Determines whether to display a "Jump backward" navigation link, allowing the user to return to the closest prior
 * unanswered element.
 */
export const hasJumpBackNavAtom = atom<boolean>((get) => {
  const indexOfPriorUnansweredElement: number | null = get(indexOfPriorUnansweredAtom);
  const currentContext: ElementContext | null = get(contextNavAtom).currentContext;

  if (!currentContext || indexOfPriorUnansweredElement === null) return false;

  return indexOfPriorUnansweredElement < currentContext.pageStart;
});

/**
 * Determines whether to display a "Jump to last" navigation link, allowing the user to move directly to the last
 * element in the current step.
 *
 * Conditions:
 * - The survey must be loaded and contain elements.
 * - The most recently answered element is the last element of the step.
 */
export const hasJumpToEndNavAtom = atom<boolean>((get) => {
  const currentContext: ElementContext | null = get(contextNavAtom).currentContext;
  if (!currentContext) return false;

  const lastAnswered: number | null = get(indexOfLastAnsweredAtom);
  const lastAnswerable: number | null = currentContext.lastAnswerableIndexInStep;

  if (lastAnswered === null || lastAnswerable === null) return false;

  return lastAnswered === lastAnswerable && lastAnswerable > currentContext.pageEnd;
});

/**
 * Determines whether to display a "Jump to first" navigation link, allowing the user to move directly to the first
 * element in the current step.
 *
 * Conditions:
 * - Must have a "jump to last" nav available
 * - Must be beyond the first item in the step
 */
export const hasJumpToBeginningNavAtom = atom<boolean>((get) => {
  const currentContext: ElementContext | null = get(contextNavAtom).currentContext;
  if (!currentContext) return false;

  const lastAnswered: number | null = get(indexOfLastAnsweredAtom);
  const currentStep: StepInfo | null = get(stepNavAtom).currentStep;
  const numberOfInstructionsOnStep: number = currentStep?.instructionCount ?? 0;
  const indexOfFirstNonInstructionOnStep: number = currentContext.stepStart + numberOfInstructionsOnStep;

  return lastAnswered === currentContext.stepEnd && currentContext.pageStart > indexOfFirstNonInstructionOnStep;
});

/**
 * Indicates whether the timer should be paused for the current element.
 * Returns `true` only if the active element is an `Instruction` and its `isTimerPaused` property is set to `true`.
 */
export const isTimerPausedAtom = atom<boolean>((get) => {
  const elementIndex: number = get(elementIndexAtom);
  const elements: Element[] = get(surveyViewAtom)?.data?.elements ?? [];

  // Guard against an unloaded/empty survey or out-of-bounds element index
  if (elementIndex < 0 || elementIndex >= elements.length) return false;

  const element: Element = elements[elementIndex];
  return ('isTimerPaused' in element) && element.isTimerPaused;
});